import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Fragment, useCallback, useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { EkasiServeAppBar, equalObj, LoadingPage, selectAllOnFocus, SetState } from "./Common";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import Snackbar from "@mui/material/Snackbar";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import { CompaniesSelect, Company } from "./Companies";
import { ClientDetails } from "./ClientDetails";
import { Container, FormControl, InputLabel, List, ListItem, ListItemButton, MenuItem, Popover, Select } from "@mui/material";
import { UserContext, userIsEmployeeOrAdmin, UserRoleAdmin } from "./Users";
import { Add, Edit } from "@mui/icons-material";
import { Quote } from "./Quotes";

export interface Client {
    id: number;
	company_id: number;
	quote_id: string;
	name: string;
    default_start_number: number;
}

function DetailsPopover({ client }: { client: Client }) {
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const auth = useContext(UserContext);
    return (
        userIsEmployeeOrAdmin(auth) 
        ? <Fragment>
            <Button aria-describedby="ClientDetails" onClick={(ev) => setAnchorEl(ev.currentTarget)} variant="contained" fullWidth>
                Details
            </Button>
            <Popover
                id="ClientDetails"
                open={anchorEl !== null}
                onClose={() => setAnchorEl(null)}
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>

                <List>
                    <ListItem>
                        <ListItemButton component={Link} to={"/clients/" + client.id + "/details"}> Show Client Details </ListItemButton>
                    </ListItem>
                    <ListItem>
                        <ListItemButton onClick={(ev) => navigate("/client-details/0", { state: {clientId: client.id || 0} })}> Create Client Detail </ListItemButton>
                    </ListItem>
                </List>
            </Popover>
        </Fragment>
        : <Button aria-describedby="ClientDetails" component={Link} to={"/clients/" + client.id + "/details"} variant="contained" fullWidth>
            Details
        </Button>
    )
}

function QuotesPopover({ client }: { client: Client }) {
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const auth = useContext(UserContext);
    return (
        userIsEmployeeOrAdmin(auth)
        ? <Fragment>
            <Button aria-describedby="Quotes" onClick={(ev) => setAnchorEl(ev.currentTarget)} variant="contained" fullWidth>
                Quotes
            </Button>
            <Popover
                id="Quotes"
                open={anchorEl !== null}
                onClose={() => setAnchorEl(null)}
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>

                <List>
                    <ListItem>
                        <ListItemButton component={Link} to={"/clients/" + client.id + "/quotes"}> Show Quotes </ListItemButton>
                    </ListItem>
                    <ListItem>
                        <ListItemButton onClick={(ev) => navigate("/quotes/0", { state: {clientIdText: client.id || 0} })}> Create Quote </ListItemButton>
                    </ListItem>
                </List>
            </Popover>
        </Fragment>
        : <Button aria-describedby="Quotes" component={Link} to={"/clients/" + client.id + "/quotes"} variant="contained" fullWidth>
            Quotes
        </Button>
    )
}

export function ClientsTable({clients, company, filterText} : { clients: Array<Client>, company: Partial<Company>, filterText: string } ) {
    const navigate = useNavigate();
    const auth = useContext(UserContext);

    return (
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid size={12}>
                <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell width="35%">Name</TableCell>
                                    <TableCell width="30%">Quote Id</TableCell>
                                    <TableCell width="15%"> 
                                    </TableCell>
                                    <TableCell width="15%"> 
                                    </TableCell>
                                    {auth.user.role === UserRoleAdmin && <TableCell width="5%">
                                        <Button variant="contained" onClick={() => navigate("/clients/0", { state: {companyId: company.id || 0} })} fullWidth>
                                            <Add />
                                        </Button>
                                    </TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {clients.map((row) => (
                                    (filterText === "" || row.name.includes(filterText)) && <TableRow key={row.id}>
                                        <TableCell>{row.name}</TableCell>
                                        <TableCell>{row.quote_id}</TableCell>
                                        <TableCell>
                                            <QuotesPopover client={row} />
                                        </TableCell>
                                        <TableCell>
                                            <DetailsPopover client={row} />
                                        </TableCell>
                                        {auth.user.role === UserRoleAdmin && <TableCell>
                                            <Button variant="contained" onClick={() => navigate("/clients/" + row.id, { state: { companyId: row.company_id || 0 }})} fullWidth>
                                                <Edit />
                                            </Button>
                                        </TableCell>}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

            </Grid>
        </Grid>
    )
}

export const ClientsPage = () => {
    var { companyIdText } = useParams();
    const [clients, setClients] = useState<Array<Client>>([]);
    const [company, setCompany] = useState<Partial<Company>>({});
    const [filterText, setFilterText] = useState("");
    const [snackbar, setSnackbar] = useState("");

    var companyId = 0;
    if (companyIdText !== undefined) {
        companyId = parseInt(companyIdText);
    }

    const [title, setTitle] = useState("");
    useEffect(() => {
        const fetchClients = async () => {
            if (companyId !== 0) {
                const companyResponse = await fetch("/api/v1/companies/" + companyId);
                const companyData = await companyResponse.json();
                setTitle(companyData?.result?.name || "");
                setCompany(companyData.result || {});
                if (companyData.error) {
                    setSnackbar(companyData.error);
                }
            } else {
                setCompany({});
            }

            const clientResponse = await fetch("/api/v1/clients?company_id=" + companyId);
            const clientData = await clientResponse.json();
            setClients(clientData.result || []);
            if (clientData.error) {
                setSnackbar(clientData.error);
            }
        };
        fetchClients();
    }, [companyId]);

    return (
        <Stack spacing={0}>
            <EkasiServeAppBar />
            <Grid component={Container} container spacing={2} sx={{ alignItems: "center" }}>
                <Grid size={8}> <h1> {title} Clients </h1> </Grid>
                <Grid size={4}> 
                    <TextField
                        id="filterText"
                        label="Search"
                        variant="outlined"
                        onChange={(e) => setFilterText(e.target.value) }
                        fullWidth
                        value={filterText} />
                </Grid>
                <Grid size={12}>
                    <ClientsTable clients={clients} filterText={filterText} company={company} />
                </Grid>
            </Grid>
            <Grid size={12} sx={{ paddingTop: 8 }}> </Grid>
            <Snackbar
                open={snackbar !== ""}
                autoHideDuration={6000}
                onClose={(ev, reason) => setSnackbar("")}
                message={snackbar}/>
        </Stack>
    )
}

export function ClientForm({ client, setClient, companies } : { client: Partial<Client>, setClient: SetState<Partial<Client>>, companies: Array<Company> }) {
    return (
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid size={{ xs: 12, md: 6 }}>
                <CompaniesSelect companies={companies} client={client} setClient={setClient} setClientDetail={() => {}}/>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                    id="name"
                    label="Client Business Name"
                    variant="outlined"
                    onFocus={selectAllOnFocus}
                    onChange={(e) => setClient(client => ({ ...client, name: e.target.value }))}
                    fullWidth
                    value={client.name || ""} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                    id="quote_id"
                    label="Quote Id"
                    variant="outlined"
                    onFocus={selectAllOnFocus}
                    onChange={(e) => setClient(client => ({ ...client, quote_id: e.target.value }))}
                    fullWidth
                    value={client.quote_id || ""} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                    id="default_start_number"
                    label="Last Used Quote Number"
                    variant="outlined"
                    onFocus={selectAllOnFocus}
                    onChange={(e) => setClient(client => ({ ...client, default_start_number: parseInt(e.target.value) || 0 }))}
                    fullWidth
                    value={client.default_start_number || ""} />
            </Grid>
        </Grid>
    )
}

export const ClientPage = () => {
    const { clientIdText } = useParams();
    const { state } = useLocation();
    const [client, setClient] = useState<Partial<Client>>({});
    const [companies, setCompanies] = useState<Array<Company>>([])
    const [snackbar, setSnackbar] = useState("");
    const [requestDelete, setRequestDelete] = useState(false);
    const [savedClient, setSavedClient] = useState<Partial<Client>>({});
    const [edited, setEdited] = useState(false);
    const [isLoading, setIsLoading] = useState(0);
    const navigate = useNavigate();

    const companyId = state?.companyId || 0;
    const clientId = parseInt(clientIdText || "0");

    useEffect(() => {
        const fetchData = async () => {
            const clientResponse = await fetch("/api/v1/clients/" + clientId);
            const clientData = await clientResponse.json();
            clientData.result.company_id = companyId;
            setClient(clientData.result || {});
            setSavedClient(clientData.result || {});
            if (clientData.error) {
                setSnackbar(clientData.error);
            }

            const companyResponse = await fetch("/api/v1/companies");
            const companyData = await companyResponse.json();
            setCompanies(companyData.result || []);
            if (companyData.error) {
                setSnackbar(companyData.error);
            }
            setIsLoading(lock => lock - 1);
        }
        setIsLoading(lock => lock + 1);
        fetchData();
    }, [clientId, companyId]);

    useEffect(() => {
        setEdited(!equalObj(client, savedClient));
    }, [client, savedClient]);

    const saveClient = useCallback(async () => {
        const response = await fetch("/api/v1/clients", {
            method: client.id === 0 ? "POST" : "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(client)
        });
        const data = await response.json();
        setClient(data.result || {});
        setSavedClient(data.result || {});
        if (data.error) {
            setSnackbar(data.error);
        } else {
            if (clientId === 0) {
                navigate("/clients/" + data.result.id, { replace: true, state: { companyId: data.result.company_id || companyId } });
            }
            setSnackbar("Information Saved");
        }
    }, [client, clientId, navigate, companyId]);

    const deleteClient = useCallback(async () => {
        const response = await fetch("/api/v1/clients/" + client.id, { method: "DELETE" });
        const data = await response.json();
        if (data.error) {
            setSnackbar(data.error);
        } else {
            setClient(data.result || {});
            navigate(-1);
        }
    }, [client.id, navigate]);

    if (isLoading) return (<LoadingPage />);

    return (
        <Stack spacing={0}>
            <EkasiServeAppBar />
            <Grid component={Container} container spacing={2} sx={{ alignItems: "center" }}>
                <Grid size={{ xs: 12, md: 8 }}> <h1> {client.name || "New Client"} </h1> </Grid>
                <Grid size={{ xs: 6, md: 2 }}>
                    {((client.id || 0) === 0)
                        ? <Button variant="contained" onClick={() => navigate(-1)} fullWidth> Cancel </Button>
                        : <Button variant="contained" onClick={() => setRequestDelete(true)} color="error" fullWidth> Delete </Button>
                    }
                </Grid>
                <Grid size={{ xs: 6, md: 2 }}>
                    <Button disabled={!edited} variant="contained" onClick={saveClient} fullWidth> Save </Button>
                </Grid>
                <Grid size={12}>
                    <ClientForm client={client} setClient={setClient} companies={companies} />
                </Grid>
            </Grid>

            <Snackbar
                open={snackbar !== ""}
                autoHideDuration={6000}
                onClose={(ev, reason) => setSnackbar("")}
                message={snackbar}/>
            <Dialog open={requestDelete}>
                <DialogTitle> Are you sure you want to delete this client? </DialogTitle>
                <Button onClick={deleteClient}> Yes, Delete </Button>
                <Button onClick={() => setRequestDelete(false)}> No, Cancel </Button>
            </Dialog>
        </Stack>

    )
}

export function ClientsSelect({ clients, clientDetail, setClientDetail, setQuote } : { clients: Array<Client>, clientDetail: Partial<ClientDetails>, setClientDetail: SetState<Partial<ClientDetails>>, setQuote: SetState<Partial<Quote>> }) {
    return (
        <FormControl fullWidth>
            <InputLabel>Client</InputLabel>
            <Select
                label="Client"
                value={clients.length === 0 ? "" : (clientDetail.client_id || "")}
                onChange={(ev) => { setQuote(quote => ({...quote, client_detail_id: undefined })); setClientDetail(clientDetail => ({ ...clientDetail, client_id: ev.target.value as number }))} }
            >
                {clients.map((client) => (
                    <MenuItem value={client.id}>{client.name}</MenuItem>
                ))}
            </Select>
        </FormControl>
    )
}